import { chunkerizeArray } from '@/helpers/chunkerizeArray'
import { truthy } from '@/helpers/truthy'

import { log } from '@/services/Log'
import { checkIsBackOrder } from '@/helpers/checkIsBackOrder'
import type { ProductType } from '@/types'
import type { BackOrderCustomFieldContent } from '@/types/Checkout'
import { arrivalWindow as GetArrivalWindow } from '@/components/Pdp/AddProductToCartForm/EstimatedArrival'

import { EXTEND_LOGO, EXTEND_SKU } from '@/data/constants'
import { checkIsExtendItem } from '@/helpers/checkIsExtendItem'
import { StandardLineItem, StandardLineItemInstructions } from '@/types/ShopFront/CheckoutStandards'
import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import checkIsShippedShieldItem from '@/helpers/checkIsShippedShieldItem'
import { isLTL, isParcel } from '../../../helpers/determineShipmentType'

type maybeFurniture = { isFurniture?: boolean }
const furnitureLast = (item1: maybeFurniture, item2:maybeFurniture) => {
  if (!item1.isFurniture && item2.isFurniture) {
    return -1
  }
  return (
    (item1.isFurniture && !item2.isFurniture)
      ? 1
      : 0
  )
}

export type PhysicalItemInputForLineItemsStardazier = {
  url: string,
  sku: string,
  productId: number
  imageUrl: string,
  parentId: null | string | number
  name: string
  salePrice: number

  brand: string
  couponAmount: number
  discountAmount: number
  extendedSalePrice: number
  id: string
  isBackOrder: boolean
  isDigital: boolean
  isExtendItem: boolean
  isFurniture: boolean
  isMutable: boolean
  isReturnable: boolean
  isShippingRequired: boolean
  isTaxable: boolean
  listPrice: number
  originalPrice: number
  quantity: number
  // type: string
  variantId: number
  arrivalWindow?: string
  options: readonly {
    name: string,
    nameId:number,
    value: string,
    valueId: number,
  }[]
  itemInstructions? : StandardLineItemInstructions
  discounts: readonly { discountedAmount: number, id: string }[]
}

export const generateLineItemsFromCheckout = async (
  cart: {
    lineItems: {
      physicalItems: PhysicalItemInputForLineItemsStardazier[]
    }
  },
  backOrderSkus?: BackOrderCustomFieldContent,
): Promise<StandardLineItem[]> => {
  log('generateLineItemsFromCheckout START', { cart })
  const lineItems = cart.lineItems.physicalItems || []
  const lineItemToSku: (p: { url: string, sku:string }) => string = ({ url, sku }) => {
    const skuFromUrl = `${url}`.split('?')[0].split('-').pop()
    return skuFromUrl || sku
  }
  const uniqueSkus = [...(new Set(
    lineItems
      // Filter out skus that are failing sku validation on product search - extended warranty & shippedshield
      .filter(({ productId, sku }) => !!productId && !!sku && !checkIsExtendItem({ sku }) && !checkIsShippedShieldItem({ sku }))
      .map(lineItemToSku),
  ))]
  const productSkus = chunkerizeArray(uniqueSkus, 25)
  // Request products skus from search api in chunks of 25
  const [{ Product }] = await allPromisesWithRetries(() => [import('@/services/Product')])
  const chunkerizedProducts: ProductType[][] = await Promise.all(productSkus.map(Product.bySkus))

  const skusData: ProductType[] = chunkerizedProducts.flat()
  const itemMetadata: { [key: string]: ProductType } = {}
  skusData.forEach((p) => {
    itemMetadata[p.id] = p
    itemMetadata[p.sku] = p
    p.variants.forEach((v) => {
      itemMetadata[v.sku] = p
    })
  })

  const itemsExtended = (
    lineItems
      .map((item) => ({
        ...item,
        isExtendItem: checkIsExtendItem(item),
      }))
      .sort((item) => (item.isExtendItem ? 1 : -1))
  )
  const ungroupedItems: StandardLineItem[] = (
    itemsExtended.map((lineItem) => {
      const { sku } = lineItem
      const product = itemMetadata[sku]
      const warrantyVariant = product?.warranty?.variants?.[sku]
      const isFurniture = isLTL(
        truthy(warrantyVariant?.is_white_glove),
        truthy(warrantyVariant?.is_entryway),
      )
      const isParcelItem = isParcel(
        truthy(warrantyVariant?.is_white_glove),
        truthy(warrantyVariant?.is_entryway),
      )
      const isFreeShipping = warrantyVariant?.is_free_shipping
      const isDigital = truthy(product?.warranty?.additional?.is_digital)
      const productVariant = product?.variants?.find((item) => lineItem.sku === item.sku)
      const imageUrl = productVariant?.images?.[0]?.url_square || lineItem?.imageUrl
      const url = `/p-${`${product?.name || 'product'}`.toLowerCase().replace(/([^a-z-])/g, ' ').trim().split(' ')[0]}-${product?.sku}?sku=${lineItem.sku}`
      const isReturnable = truthy(warrantyVariant?.is_returnable)
      const isBackOrder = (
        Array.isArray(backOrderSkus)
          ? !!backOrderSkus.find((i) => (
            i?.sku === sku
            || i as unknown as string === sku
          ))
          : checkIsBackOrder(warrantyVariant)
      )

      let arrivalWindow = (lineItem.arrivalWindow || '')
      let completeArrivalWindow = ''

      // no arrival window calculation for shippedshield item
      if (!checkIsShippedShieldItem({ sku: lineItem.sku })) {
        if (lineItem.isExtendItem && !arrivalWindow) {
          const skuOfItemExtending = sku.split(EXTEND_SKU).pop()
          arrivalWindow = itemsExtended.find((item) => item.sku === skuOfItemExtending)?.arrivalWindow || ''
        } else if (!arrivalWindow) {
          arrivalWindow = (
            Array.isArray(backOrderSkus)
            && isBackOrder
            && backOrderSkus.find((i) => i.sku === lineItem.sku)?.arrivalWindow
          ) || GetArrivalWindow({
            variant: warrantyVariant,
          })
        }

        completeArrivalWindow = GetArrivalWindow({
          variant: warrantyVariant,
          toDateTimeRange: true,
        })
      }
      const couponAmount = (
        lineItem.discounts.reduce((acc, { discountedAmount }) => acc + discountedAmount, 0)
      )

      const item: StandardLineItem = {
        ...lineItem,
        couponAmount,
        subtotalPrice: lineItem.listPrice,
        options: [...(lineItem.options || [])],
        discounts: [...lineItem.discounts],
        extendedListPrice: lineItem.salePrice,
        parentId: lineItem.parentId || null,
        originalName: lineItem.name,
        salePriceAfterDiscount: lineItem.salePrice,
        url,
        imageUrl: lineItem.isExtendItem ? EXTEND_LOGO : imageUrl,
        // originalPrice: lineItem.salePrice,
        isFurniture,
        giftWrapping: null,
        isReturnable,
        isDigital,
        isBackOrder,
        isParcelItem,
        arrivalWindow,
        completeArrivalWindow,
        isExtendItem: lineItem.isExtendItem,
        overSizeCharge: 0,
        isMutable: false,
        isFreeShipping,
      }
      return item
    })
  )
  // Sorting furniture to the end to match output from LineItemsFromCheckout
  const response = ungroupedItems.sort(furnitureLast)

  log('generateLineItemsFromCheckout END', { response })
  return response
}

export default generateLineItemsFromCheckout
